import Cookies from 'js-cookie';

import { sendTrack } from './utilities/track';

document.addEventListener('gform/conditionalLogic/init/end', () => {
  validateRequiredFields();
  requestAnimationFrame(() => revealFormButtons());
});

// Forms without conditional logic never fire init/end — reveal their buttons from post_render.
document.addEventListener('gform/post_render', (e) => {
  const formId = String(e.detail?.formId || '');
  const wrapper = document.querySelector(`#gform_wrapper_${formId}`);
  if (!wrapper || wrapper.classList.contains('has_conditional_logic')) return;
  validateRequiredFields();
  requestAnimationFrame(() => revealFormButtons(true));
});

document.addEventListener('DOMContentLoaded', () => {
  init();

  document.addEventListener('gform/post_render', (e) => {
    onGFormPostRender(e.detail?.currentPage);
  });
});

// -------------------------------------------------------------------------
// Stores the real, unstripped browser URL (with UTM params intact) in a
// cookie every page load. gf-functions/page-url-field.php reads this cookie
// server-side to natively populate a GF field with "Allow field to be
// populated dynamically" set to `page_url` (gform_field_value_page_url), the
// same pattern segment-anon-cookie.php uses for segmentanonid. GF's own
// source_url capture instead reads $_SERVER['REQUEST_URI'], which passes
// through Pantheon's edge (values outside its cache allowlist come through
// as "PANTHEON_STRIPPED") and is truncated to 200 chars — a cookie set here
// client-side avoids both.
// -------------------------------------------------------------------------
try {
  Cookies.set('unlk_current_page_url', globalThis.location.href, { path: '/', sameSite: 'Lax' });
} catch {}

document.addEventListener('change', (e) => {
  const radio = e.target;
  if (!radio.matches('input[type="radio"]')) return;
  if (!radio.closest('.avm-new-construction-answer')) return;

  const form = radio.closest('form[id^="gform_"]');
  const formId = form ? String(form.dataset.formid || form.id.replace(/^gform_/, '')) : '';
  const formName = form?.dataset?.adminLabel || '';

  sendTrack('New Construction Verified', {
    // eslint-disable-next-line camelcase
    verification_status: radio.value.toLowerCase() === 'yes',
    // eslint-disable-next-line camelcase
    form_id: formId,
    // eslint-disable-next-line camelcase
    form_name: formName,
    path: globalThis.location.pathname,
    url: globalThis.location.href,
    title: document.title,
  });
});

document.addEventListener('click', (e) => {
  const btn = e.target.closest(
    '[id^="gform_next_button_"], .gform_next_button, .gform_button[type="submit"], [data-submission-type="submit"]'
  );
  if (!btn) return;

  const page = btn.closest('.gform_page');
  if (!page || !page.classList.contains('avm-property-estimate-page')) return;

  trackPropertyFinancialsEntered(page, btn);
});

function shouldFirePropertyFinancials(certaintyRating, lastSalePrice, customerStatedValue) {
  return certaintyRating === 'no_certainty' && lastSalePrice === null && Boolean(customerStatedValue);
}

function resolveLastSalePrice(isNewConstruction, certaintyRating, rawValue) {
  if (isNewConstruction === 'yes' && certaintyRating === 'low' && rawValue) {
    const num = Number.parseInt(rawValue, 10);

    if (!Number.isNaN(num)) return num;
  }

  return null;
}

function avmCertaintyRating(holder) {
  if (!holder) return 'no_certainty';
  if (holder.classList.contains('high')) return 'high';
  if (holder.classList.contains('low')) return 'low';
  return 'no_certainty';
}

function trackPropertyFinancialsEntered(page, btn) {
  const form = page.closest('form[id^="gform_"]') || btn.closest('form[id^="gform_"]');
  const formId = form ? String(form.dataset.formid || form.id.replace(/^gform_/, '')) : '';
  const formName = form?.dataset?.adminLabel || '';

  // Certainty is encoded as a CSS class on .avm-holder (no-certainty, high, low)
  const avmHolder = page.querySelector('.avm-holder');
  const certaintyRating = avmCertaintyRating(avmHolder);

  // AVM-estimated value shown in the result (null when no_certainty)
  const valueEl = avmHolder?.querySelector('.value');
  let estimatedHomeValue = null;
  if (valueEl && !valueEl.classList.contains('empty')) {
    const num = Number.parseInt(valueEl.textContent.replaceAll(/[^0-9]/g, ''), 10);
    if (!Number.isNaN(num)) estimatedHomeValue = num;
  }

  // User-entered or pre-filled value from the estimated-value number input
  const estimatedInput = page.querySelector('.estimated-value input');
  const rawValue = estimatedInput ? estimatedInput.value.replaceAll(/[^0-9]/g, '') : '';
  const customerStatedValue = rawValue || null;

  // NC radio answer (field lives on a different hidden page, still in DOM)
  const ncChecked = document.querySelector('.avm-new-construction-answer input[type="radio"]:checked');
  const isNewConstruction = ncChecked ? ncChecked.value.toLowerCase() : null;

  const lastSalePrice = resolveLastSalePrice(isNewConstruction, certaintyRating, rawValue);

  if (!shouldFirePropertyFinancials(certaintyRating, lastSalePrice, customerStatedValue)) return;

  sendTrack('Property Financials Entered', {
    estimatedHomeValue,
    // eslint-disable-next-line camelcase
    customer_stated_value: customerStatedValue,
    // eslint-disable-next-line camelcase
    certainty_rating: certaintyRating,
    // eslint-disable-next-line camelcase
    is_new_construction: isNewConstruction,
    // eslint-disable-next-line camelcase
    last_sale_price: lastSalePrice,
    // eslint-disable-next-line camelcase
    form_id: formId,
    // eslint-disable-next-line camelcase
    form_name: formName,
    path: globalThis.location.pathname,
    url: globalThis.location.href,
    title: document.title,
  });
}

function onGFormPostRender(currentPage) {
  mirrorSubmitButtonText();
  cleanDuplicateFaqContainers();
  formatMoneyInput();
  forceCursorPlacement();
  attachRequiredListeners();
  attachRangeValidation();
  // Delay validation to ensure GF has finished page transition and DOM updates
  requestAnimationFrame(() => validateRequiredFields());
  setTimeout(() => validateRequiredFields(), 250);
  setTimeout(() => validateRequiredFields(), 750);

  // First/Last name validation text
  const firstNameMessages = document.querySelectorAll(
    '.ginput_container_name.has_first_name ~ .text-danger.small.validation_message'
  );
  for (const el of firstNameMessages) {
    el.textContent = 'First name required';
  }

  const lastNameMessages = document.querySelectorAll(
    '.ginput_container_name.has_last_name ~ .text-danger.small.validation_message'
  );
  for (const el of lastNameMessages) {
    el.textContent = 'Last name required';
  }

  for (const wrapper of document.querySelectorAll('.gform_wrapper')) {
    const formId = wrapper.id.replace('gform_wrapper_', '');
    const page = getActivePage(wrapper);
    if (page) insertAfterPage(page, formId);
  }

  insertFaqsForPage(currentPage);

  // Move modals to <body> (rough analog to `.modal:visible` block)
  for (const modal of document.querySelectorAll('.modal')) {
    document.body.append(modal);
  }

  // Add numeric pattern to certain inputs
  for (const input of document.querySelectorAll('input')) {
    const type = input.getAttribute('type');
    const autocomplete = input.getAttribute('autocomplete');
    const parent = input.parentElement;

    if (
      type === 'number' ||
      autocomplete === 'postal-code' ||
      (parent && parent.classList.contains('ginput_container_number'))
    ) {
      input.setAttribute('inputmode', 'numeric');
    }
  }
}

function init() {
  formatMoneyInput();
  gformPageCount();
  gformSelect();
  gformSpinner();
  gformRequiredValidation();
  gravityContainerRelocate();
  initFaqContainers();
  mirrorSubmitButtonText();
}

// -------------------------------------------------------------------------
// Keep any [data-mirror-submit-text] element (e.g. quoted button text in a
// consent field description) in sync with the form's actual submit/next
// button label — whatever set it: the default text from the GF form editor,
// GF Conditional Submit Text, the OTP "Verify Code" swap, or GF's own
// Next→Submit button conversion on the last page.
// -------------------------------------------------------------------------

function findMirrorableButton(wrapper) {
  return wrapper.querySelector(
    '.gform_button[type="submit"], button[data-submission-type="submit"]'
  );
}

function syncMirroredSubmitText(wrapper) {
  const mirrors = wrapper.querySelectorAll('[data-mirror-submit-text]');
  if (!mirrors.length) return;

  const btn = findMirrorableButton(wrapper);
  if (!btn) return;

  const text = (btn.tagName === 'INPUT' ? btn.value : btn.textContent).trim();
  if (!text) return;

  for (const el of mirrors) {
    if (el.textContent.trim() !== text) el.textContent = text;
  }
}

// A MutationObserver on the whole wrapper catches every way the button text
// can change (any plugin's textContent/value write, or GF swapping which
// element is the submit button) without needing to hook each source.
function watchMirroredSubmitText(wrapper) {
  if (wrapper.dataset.mirrorSubmitWatching) return;
  if (!wrapper.querySelector('[data-mirror-submit-text]')) return;
  wrapper.dataset.mirrorSubmitWatching = '1';

  syncMirroredSubmitText(wrapper);

  let scheduled = false;
  const scheduleSync = () => {
    if (scheduled) return;
    scheduled = true;
    queueMicrotask(() => {
      scheduled = false;
      syncMirroredSubmitText(wrapper);
    });
  };

  const observer = new MutationObserver(scheduleSync);
  observer.observe(wrapper, {
    childList: true,
    characterData: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-submission-type', 'class', 'value'],
  });
}

function mirrorSubmitButtonText() {
  for (const wrapper of document.querySelectorAll('.gform_wrapper')) {
    watchMirroredSubmitText(wrapper);
  }
}

// -------------------------------------------------------------------------
// Disable Next/Submit until all required fields on the current page are filled,
// with hover tooltip (desktop) and click-to-reveal inline errors (mobile/fallback).
// -------------------------------------------------------------------------

function gformRequiredValidation() {
  attachRequiredListeners();
  validateRequiredFields();
}

function attachRequiredListeners() {
  for (const wrapper of document.querySelectorAll('.gform_wrapper')) {
    if (wrapper.dataset.requiredInit) continue;
    wrapper.dataset.requiredInit = '1';

    const revalidateFromEvent = (e) => {
      clearErrorFromEvent(e);
      validateRequiredFields(wrapper);
      requestAnimationFrame(() => validateRequiredFields(wrapper));
      setTimeout(() => validateRequiredFields(wrapper), 150);
    };

    wrapper.addEventListener('input', revalidateFromEvent);
    wrapper.addEventListener('change', revalidateFromEvent);
    wrapper.addEventListener('keyup', revalidateFromEvent);
    wrapper.addEventListener('blur', () => {
      validateRequiredFields(wrapper);
      requestAnimationFrame(() => validateRequiredFields(wrapper));
      setTimeout(() => validateRequiredFields(wrapper), 150);
    }, true);
    wrapper.addEventListener('paste', (e) => {
      clearErrorFromEvent(e);
      requestAnimationFrame(() => validateRequiredFields(wrapper));
      setTimeout(() => validateRequiredFields(wrapper), 150);
    });
  }
}

function getActivePage(wrapper) {
  const pages = wrapper.querySelectorAll('.gform_page');
  if (pages.length === 0) return wrapper.querySelector('.gform_body');

  for (const page of pages) {
    if (page.offsetParent !== null && getComputedStyle(page).display !== 'none') return page;
  }

  return null;
}

function validateRequiredFields(wrapper) {
  const wrappers = wrapper ? [wrapper] : document.querySelectorAll('.gform_wrapper');

  for (const w of wrappers) {
    const page = getActivePage(w);
    if (!page) continue;

    const nextBtn = page.querySelector('.gform_next_button');
    const submitBtn = w.querySelector(
      '.gform_button[type="submit"], button[data-submission-type="submit"]'
    );
    const btn = nextBtn || submitBtn;
    if (!btn) continue;

    const invalid = getInvalidRequiredFields(page);
    const allFilled = invalid.length === 0;

    setButtonInvalidState(btn, !allFilled);
    attachButtonHandlers(btn, w);
  }
}

function getInvalidRequiredFields(page) {
  const invalid = [];
  const required = page.querySelectorAll(
    '.gfield_contains_required:not(.gfield_visibility_hidden)'
  );

  for (const field of required) {
    const style = getComputedStyle(field);
    if (
      field.hidden ||
      field.getAttribute('aria-hidden') === 'true' ||
      style.display === 'none' ||
      style.visibility === 'hidden' ||
      field.classList.contains('gf_hidden')
    ) {
      continue;
    }

    if (!isFieldFilled(field)) invalid.push(field);
  }

  return invalid;
}

function getFieldRangeError(field) {
  const input = field.querySelector(
    'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([type="file"])'
  );
  if (!input) return null;

  const min = 'rangeMin' in input.dataset ? Number(input.dataset.rangeMin)
    : input.hasAttribute('min') ? Number(input.getAttribute('min')) : null;
  const max = 'rangeMax' in input.dataset ? Number(input.dataset.rangeMax)
    : input.hasAttribute('max') ? Number(input.getAttribute('max')) : null;
  if (min === null && max === null) return null;

  const raw = (input.value || '').replaceAll(/[^0-9]/g, '');
  if (!raw) return null;

  const num = Number.parseInt(raw, 10);
  if (Number.isNaN(num)) return null;

  if ((min !== null && num < min) || (max !== null && num > max)) {
    const minStr = min === null ? null : numberWithCommas(String(min));
    const maxStr = max === null ? null : numberWithCommas(String(max));
    if (minStr && maxStr) return `Please enter a number from ${minStr} to ${maxStr}.`;
    if (minStr) return `Please enter a number of at least ${minStr}.`;
    return `Please enter a number of at most ${maxStr}.`;
  }

  return null;
}

function isInputHidden(input) {
  const style = getComputedStyle(input);
  if (
    input.disabled ||
    input.hidden ||
    input.getAttribute('aria-hidden') === 'true' ||
    style.display === 'none' ||
    style.visibility === 'hidden'
  ) return true;
  if (input.closest('.gfield_visibility_hidden')) return true;
  const col = input.closest('.gform-grid-col');
  return col !== null && getComputedStyle(col).display === 'none';
}

function isInputValueValid(input) {
  const val = input.tagName === 'SELECT' ? input.value : input.value.trim();
  if (!val) return false;
  if (input.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) return false;
  if (input.type === 'tel' && val.replaceAll(/\D/g, '').length < 10) return false;
  return true;
}

function isFieldFilled(field) {
  // Checkbox, radio, consent — at least one must be checked
  const checks = field.querySelectorAll(
    'input[type="checkbox"]:not([id*="select_all"]), input[type="radio"]'
  );
  if (checks.length > 0) {
    return [...checks].some((c) => c.checked);
  }

  // File upload — check if a file is selected or already uploaded
  const fileInput = field.querySelector('input[type="file"]');
  if (fileInput) {
    return Boolean(
      fileInput.value ||
      field.querySelector(
        '.ginput_preview, .gform_fileupload_multifile .gfield_fileupload_filename'
      )
    );
  }

  // Multi-input fields (address, name, date, time) — only check aria-required inputs.
  // Simple fields — fall back to all visible inputs/selects/textareas.
  let inputs = field.querySelectorAll('[aria-required="true"]:not([type="hidden"])');

  if (inputs.length === 0) {
    inputs = field.querySelectorAll(
      'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([type="file"]), select, textarea'
    );
  }

  for (const input of inputs) {
    if (isInputHidden(input)) continue;
    if (!isInputValueValid(input)) return false;
  }

  if (getFieldRangeError(field) !== null) return false;

  return true;
}

// Native `disabled` blocks `click` events, but `mouseenter`/`pointerdown` still
// fire when `pointer-events: auto` is set — so we keep the native attribute for
// submission-blocking and use those events to drive tooltip + tap feedback.
function setButtonInvalidState(btn, isInvalid) {
  btn.disabled = isInvalid;
  btn.style.pointerEvents = isInvalid ? 'auto' : '';
  if (!isInvalid) hideTooltip();
}

// immediateNext: true when called from post_render (no conditional logic) — reveal
// next buttons directly. false (default) for init/end — check page visibility to
// decide whether the next button will be converted to submit by gform-pagination.
function revealFormButtons(immediateNext = false) {
  for (const w of document.querySelectorAll('.gform_wrapper')) {
    const page = getActivePage(w);
    if (!page) continue;
    const nextBtn = page.querySelector('.gform_next_button');
    const submitBtn = w.querySelector(
      '.gform_button[type="submit"], button[data-submission-type="submit"]'
    );
    const btn = (nextBtn && getComputedStyle(nextBtn).display !== 'none') ? nextBtn : submitBtn;
    if (!btn) continue;
    if (!immediateNext && btn.classList.contains('gform_next_button')) {
      // At init/end time GF has already applied conditional logic — check if this
      // page is the last visible one. If so, pre-convert the button ourselves so it
      // shows immediately as Submit rather than waiting for gform-pagination's async
      // config fetch (~300ms). gform-pagination will re-apply the same styles later
      // (no visible effect). If not the last page, reveal as Next immediately.
      btn.classList.remove('gf-btn-init');
      if (_isLastVisiblePage(w) && submitBtn) {
        _preConvertToSubmit(nextBtn, submitBtn);
      }

      validateRequiredFields();
      btn.classList.add('gf-btn-init');
    } else {
      btn.classList.add('gf-btn-init');
    }

    _watchButton(btn);
  }
}

// Returns true when the currently active page is the last page that conditional
// logic leaves visible — meaning gform-pagination will convert the next button to
// a submit button. Mirrors gform-pagination's c() page-state calculation.
function _isLastVisiblePage(wrapper) {
  const pages = wrapper.querySelectorAll('.gform_page');

  if (pages.length <= 1) return false;

  let lastVisible = 0;
  let currentVisible = 0;
  let i = 0;

  for (const p of pages) {
    i++;

    if (p.getAttribute('data-conditional-logic') !== 'hidden') {
      lastVisible = i;

      if (p.style.display !== 'none') currentVisible = i;
    }
  }

  return currentVisible > 0 && currentVisible === lastVisible;
}

// Copies submit-button presentation onto the next button so it renders correctly
// before gform-pagination's async init finishes. Saves the original values using
// the same dataset keys gform-pagination uses so its own restore (y()) still works.
function _preConvertToSubmit(nextBtn, submitBtn) {
  if (nextBtn.dataset.originalType === undefined) {
    nextBtn.dataset.originalType = nextBtn.type;
    nextBtn.dataset.originalClass = nextBtn.className;
    nextBtn.dataset.originalSubmissionType = nextBtn.dataset.submissionType || 'submit';
    if (nextBtn.tagName === 'BUTTON') {
      nextBtn.dataset.originalInnerHtml = nextBtn.innerHTML;
    } else {
      nextBtn.dataset.originalValue = nextBtn.value;
    }
  }

  nextBtn.type = submitBtn.type;
  nextBtn.className = submitBtn.className;
  nextBtn.dataset.submissionType = submitBtn.dataset.submissionType || 'submit';
  if (nextBtn.tagName === 'BUTTON') {
    nextBtn.innerHTML = submitBtn.innerHTML;
  } else {
    nextBtn.value = submitBtn.value;
  }
}

function _watchButton(btn) {
  if (btn._gfWatching) return;
  btn._gfWatching = true;

  const obs = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.attributeName === 'class' && !btn.classList.contains('gf-btn-init')) {
        // gform-pagination replaced the class (next→submit conversion or page change) —
        // re-add gf-btn-init and re-validate before the next paint.
        queueMicrotask(() => {
          if (!btn.classList.contains('gf-btn-init')) {
            btn.classList.add('gf-btn-init');
            validateRequiredFields();
          }
        });
      }
    }
  });
  obs.observe(btn, { attributes: true, attributeFilter: ['class', 'style', 'disabled', 'type'] });
}

function attachButtonHandlers(btn, wrapper) {
  if (btn.dataset.validationInit) return;
  btn.dataset.validationInit = '1';

  btn.addEventListener('mouseenter', () => {
    if (!btn.disabled) return;
    const page = btn.closest('.gform_page') || getActivePage(wrapper);
    if (!page) return;
    const invalid = getInvalidRequiredFields(page);
    if (invalid.length > 0) showTooltip(btn, invalid);
  });

  btn.addEventListener('mouseleave', hideTooltip);
  btn.addEventListener('blur', hideTooltip);

  // `pointerdown` fires on disabled buttons (unlike `click`) and covers both
  // mouse and touch — acts as the tap/click trigger on mobile and as a
  // fallback when hover isn't available.
  btn.addEventListener('pointerdown', () => {
    if (!btn.disabled) return;
    hideTooltip();

    const page = btn.closest('.gform_page') || getActivePage(wrapper);
    if (!page) return;

    const invalid = getInvalidRequiredFields(page);
    if (invalid.length === 0) return;

    for (const field of invalid) showFieldError(field);

    const form = btn.closest('form');
    if (form) {
      const pageMatch = (page.id || '').match(/_(\d+)$/);
      form.dispatchEvent(
        new CustomEvent('unlock/form/client_error', {
          bubbles: true,
          detail: {
            formId: form.dataset.formid || form.id.replace(/^gform_/, ''),
            currentPage: pageMatch ? Number(pageMatch[1]) : undefined,
            fields: invalid.map((f) => getFieldLabel(f) || 'Required field'),
            trigger: btn.classList.contains('gform_next_button')
              ? 'next_disabled'
              : 'submit_disabled'
          }
        })
      );
    }

    const first = invalid[0];
    first.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const firstInput = first.querySelector(
      'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]), select, textarea'
    );
    if (firstInput) {
      try {
        firstInput.focus({ preventScroll: true });
      } catch {
        firstInput.focus();
      }
    }
  });
}

// -------------------------------------------------------------------------
// Tooltip (desktop hover)
// -------------------------------------------------------------------------

let activeTooltip = null;

function showTooltip(btn, invalid) {
  hideTooltip();

  const tip = document.createElement('div');
  tip.className = 'gform-validation-tooltip';
  tip.setAttribute('role', 'tooltip');

  const heading = document.createElement('div');
  heading.className = 'gform-validation-tooltip-heading';
  heading.textContent =
    invalid.length === 1 ? 'Please complete:' : 'Please complete the following:';
  tip.append(heading);

  const list = document.createElement('ul');
  list.className = 'gform-validation-tooltip-list';
  for (const field of invalid) {
    const li = document.createElement('li');
    li.textContent = getFieldLabel(field) || 'Required field';
    list.append(li);
  }

  tip.append(list);

  Object.assign(tip.style, {
    position: 'fixed',
    zIndex: '9999',
    background: '#1f2937',
    color: '#fff',
    padding: '8px 12px',
    borderRadius: '4px',
    fontSize: '13px',
    lineHeight: '1.4',
    maxWidth: '280px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
    pointerEvents: 'none',
    opacity: '0'
  });

  const listEl = tip.querySelector('.gform-validation-tooltip-list');
  Object.assign(listEl.style, { margin: '4px 0 0', padding: '0 0 0 18px' });

  document.body.append(tip);

  const btnRect = btn.getBoundingClientRect();
  const tipRect = tip.getBoundingClientRect();
  const margin = 8;

  let top = btnRect.top - tipRect.height - margin;
  if (top < margin) top = btnRect.bottom + margin;

  let left = btnRect.left + (btnRect.width - tipRect.width) / 2;
  left = Math.max(margin, Math.min(left, window.innerWidth - tipRect.width - margin));

  tip.style.top = `${top}px`;
  tip.style.left = `${left}px`;
  tip.style.opacity = '1';

  activeTooltip = tip;
}

function hideTooltip() {
  if (activeTooltip) {
    activeTooltip.remove();
    activeTooltip = null;
  }
}

// -------------------------------------------------------------------------
// Inline field errors
// -------------------------------------------------------------------------

function getFieldLabel(field) {
  const labelEl = field.querySelector('.gfield_label');
  const labelText = labelEl ? extractLabelText(labelEl) : '';
  const labelIsHidden = labelEl && labelEl.classList.contains('screen-reader-text');

  if (labelText && !labelIsHidden) return labelText;

  const input = field.querySelector(
    'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]), select, textarea'
  );
  if (input) {
    // Bootstrap floating labels: <div class="form-floating"><input id="x"><label for="x">Email</label></div>
    if (input.id) {
      const floating = field.querySelector(`.form-floating label[for="${CSS.escape(input.id)}"]`);
      if (floating) {
        const txt = extractLabelText(floating);
        if (txt) return txt;
      }
    }

    const placeholder = (input.placeholder || '').trim();
    if (placeholder) return placeholder;

    const aria = (input.getAttribute('aria-label') || '').trim();
    if (aria) return aria;
  }

  return labelText || '';
}

function extractLabelText(labelEl) {
  const clone = labelEl.cloneNode(true);
  for (const r of clone.querySelectorAll(
    '.gfield_required, .gfield_required_indicator, .gfield_required_text'
  )) {
    r.remove();
  }

  return clone.textContent.replaceAll(/\s+/g, ' ').trim();
}

function attachRangeValidation() {
  for (const field of document.querySelectorAll('.gfield.estimated-value')) {
    const input = field.querySelector('input:not([type="hidden"])');
    if (!input || input._rangeInit) continue;
    input._rangeInit = true;

    input.addEventListener('blur', () => {
      if (!input.value.trim()) return;
      const err = getFieldRangeError(field);
      if (err) showFieldError(field);
      else clearFieldError(field);
    });

    input.addEventListener('input', () => {
      if (!input.value.trim()) { clearFieldError(field); return; }
      const err = getFieldRangeError(field);
      if (err) showFieldError(field);
      else clearFieldError(field);
    });
  }
}

function showFieldError(field) {
  const rangeError = getFieldRangeError(field);
  const label = getFieldLabel(field);
  const message = rangeError || (label ? `${label} is required` : 'This field is required');

  field.classList.add('gfield_error', 'field-invalid');

  for (const input of field.querySelectorAll('input:not([type="hidden"]), select, textarea')) {
    input.classList.add('is-invalid');
  }

  let errEl = field.querySelector(':scope > .gfield-inline-error');
  if (!errEl) {
    errEl = document.createElement('div');
    errEl.className = 'gfield-inline-error text-danger small mt-1';
    errEl.setAttribute('role', 'alert');
    const container = field.querySelector('.ginput_container') || field.lastElementChild || field;
    container.after(errEl);
  }

  errEl.textContent = message;
}

function clearFieldError(field) {
  field.classList.remove('gfield_error', 'field-invalid');
  for (const input of field.querySelectorAll('.is-invalid')) {
    input.classList.remove('is-invalid');
  }

  const errEl = field.querySelector(':scope > .gfield-inline-error');
  if (errEl) errEl.remove();
}

function clearErrorFromEvent(e) {
  const input = e.target;
  if (!input || !['INPUT', 'SELECT', 'TEXTAREA'].includes(input.tagName)) return;
  const field = input.closest('.gfield_contains_required');
  if (field) clearFieldError(field);
}

// -------------------------------------------------------------------------

const SPINNER_OVERLAY =
  '<div role="status" class="spinner-home"><span class="visually-hidden">Loading...</span></div>';

// Shown only on the final submit (not "next" page) — that's the request that
// hits the Max API and can take a few seconds. Purely cosmetic/simulated;
// not tied to actual backend progress.
const SUBMIT_STATUS_MESSAGES = [
  'Submitting your details...',
  'Verifying your address...',
  'Fetching property details...',
  'Setting up your account...',
  'Finalizing your offer...',
  'Almost done...'
];

function isFinalSubmitButton(btn) {
  if (!btn) return false;
  const isSubmitType = btn.matches('.gform_button[type="submit"], button[data-submission-type="submit"]');
  return isSubmitType && !btn.classList.contains('gform_next_button') && !btn.classList.contains('gform_previous_button');
}

function startSubmitStatusMessages(wrapper) {
  const overlay = wrapper.querySelector('.gform-spinner-overlay');
  if (!overlay) return;

  overlay.classList.add('has-status');

  const status = document.createElement('div');
  status.className = 'gform-spinner-status';
  status.textContent = SUBMIT_STATUS_MESSAGES[0];
  overlay.append(status);

  let i = 0;
  wrapper._submitStatusInterval = setInterval(() => {
    i += 1;
    if (i >= SUBMIT_STATUS_MESSAGES.length) {
      clearInterval(wrapper._submitStatusInterval);
      wrapper._submitStatusInterval = null;
      return;
    }

    status.textContent = SUBMIT_STATUS_MESSAGES[i];
  }, 1100);
}

function stopSubmitStatusMessages(wrapper) {
  if (wrapper?._submitStatusInterval) {
    clearInterval(wrapper._submitStatusInterval);
    wrapper._submitStatusInterval = null;
  }
}

function showSpinnerOverlay(wrapper, btn) {
  if (!wrapper || wrapper.querySelector('.gform-spinner-overlay')) return;
  wrapper.insertAdjacentHTML('beforeend', `<div class="gform-spinner-overlay">${SPINNER_OVERLAY}</div>`);
  if (isFinalSubmitButton(btn)) startSubmitStatusMessages(wrapper);
}

function hideSpinnerOverlay(wrapper) {
  wrapper?.querySelector('.gform-spinner-overlay')?.remove();
  stopSubmitStatusMessages(wrapper);
}

function gformSpinner() {
  // GF 3.0 adds/removes .gform-has-spinner on the button during AJAX — observe that
  // instead of trying to override gform.spinner.show (which GF overwrites after our init).
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type !== 'attributes') continue;
      const el = mutation.target;
      const wrapper = el.closest('.gform_wrapper');
      if (!wrapper) continue;
      if (el.classList.contains('gform-has-spinner')) {
        showSpinnerOverlay(wrapper, el);
      } else if (!wrapper.querySelector('.gform-has-spinner')) {
        hideSpinnerOverlay(wrapper);
      }
    }
  });

  function observeWrapper(wrapper) {
    observer.observe(wrapper, { attributes: true, attributeFilter: ['class'], subtree: true });
  }

  for (const wrapper of document.querySelectorAll('.gform_wrapper')) {
    observeWrapper(wrapper);
  }

  // Handle forms rendered after page load
  document.addEventListener('gform/post_render', (e) => {
    const formId = String(e.detail?.formId || '');
    const wrapper = document.getElementById(`gform_wrapper_${formId}`);
    if (wrapper) {
      hideSpinnerOverlay(wrapper);
      observeWrapper(wrapper);
    }
  });
}

function gformPageCount() {
  if (!document.querySelector('.gform_wrapper')) return;

  // eslint-disable-next-line no-undef
  if (typeof gform === 'undefined' || typeof gform.addAction !== 'function') return;

  // Gravity Forms JS API
  // eslint-disable-next-line no-undef
  gform.addAction(
    'gform_frontend_pages_evaluated',
    (pages) => {
      let count = 1;
      for (const page of pages) {
        if (page.isVisible) {
          count++;
        }
      }

      const stepTotals = document.querySelectorAll('.gf_page_steps .gf_step_total');
      for (const el of stepTotals) {
        el.textContent = String(count);
      }
    },
    10,
    3
  );
}

function gformSelect() {
  if (!document.querySelector('.gform_wrapper')) return;

  // eslint-disable-next-line no-undef
  if (typeof gform === 'undefined' || typeof gform.addAction !== 'function') return;

  // eslint-disable-next-line no-undef
  gform.addAction(
    'gform_input_change',
    (elem) => {
      if (elem && elem.tagName === 'SELECT') {
        elem.classList.toggle('empty', elem.options.selectedIndex === 0);
      }
    },
    10
  );
}

function formatMoneyInput() {
  const formatMoneyInputs = document.querySelectorAll('.format-money input, input.format-money');

  for (const input of formatMoneyInputs) {
    input.addEventListener('input', () => {
      const raw = (input.value || '').toString().replaceAll(/[^0-9]/g, '');

      if (!raw.length) {
        input.value = '';
        return;
      }

      let num = Number.parseInt(raw, 10);
      if (Number.isNaN(num)) num = 0;

      const maxAttr = input.getAttribute('data-max');
      let max = null;

      if (maxAttr !== null && maxAttr !== '') {
        const maxNum = Number.parseInt(maxAttr.toString().replaceAll(/[^0-9]/g, ''), 10);
        if (!Number.isNaN(maxNum)) max = maxNum;
      }

      if (max !== null && num > max) num = max;

      const formatted = numberWithCommas(num.toString());
      input.value = `$${formatted}`;
    });
  }
}

function numberWithCommas(x) {
  return x.toString().replaceAll(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function forceCursorPlacement() {
  const inputsMasked = document.querySelectorAll('.gfield.force-cursor input');

  for (const input of inputsMasked) {
    input.addEventListener('mouseup', (e) => {
      const val = e.currentTarget.value.replaceAll(/[()._-]/g, '').trim();
      if (val === '') {
        e.currentTarget.setSelectionRange(0, 0);
      }
    });
  }
}

function gravityContainerRelocate() {
  for (const container of document.querySelectorAll('.gravity-container')) {
    const row = container.closest('.gravity-column')?.querySelector('.row');
    if (row) row.after(container);
    container.classList.add('is-ready');
  }
}

function initFaqContainers() {
  for (const el of document.querySelectorAll('.gf-faqs')) {
    const wrapper = document.getElementById(`gform_wrapper_${el.dataset.formId}`);
    if (wrapper?.parentNode) {
      el.style.display = 'none';
      wrapper.parentNode.insertBefore(el, wrapper.nextSibling);
    }
  }

  insertFaqsForPage(1);
}

function cleanDuplicateFaqContainers() {
  for (const wrapper of document.querySelectorAll('.gform_wrapper')) {
    for (const el of wrapper.querySelectorAll('.gf-faqs')) {
      const { faqPage, formId } = el.dataset;

      // On AJAX re-render the server injects fresh FAQs inside the new wrapper.
      // Remove the stale outside master so the fresh element replaces it.
      for (const stale of document.querySelectorAll(
        `.gf-faqs[data-faq-page="${faqPage}"][data-form-id="${formId}"]`
      )) {
        if (!stale.closest('.gform_wrapper')) {
          stale.remove();
          break;
        }
      }

      el.style.display = 'none';
      wrapper.parentNode.insertBefore(el, wrapper.nextSibling);
    }
  }
}

function insertFaqsForPage(currentPage = 1) {
  const page = currentPage || 1;

  for (const el of document.querySelectorAll(`.gf-faqs[data-faq-page="${page}"]`)) {
    if (el.closest('.gform_wrapper')) continue;

    const { formId } = el.dataset;
    const gfPage = document.getElementById(`gform_page_${formId}_${page}`);

    if (!gfPage) continue;

    const footer = gfPage.querySelector('.gform_page_footer');

    if (!footer) continue;

    el.style.display = '';
    footer.after(el);
  }
}

function insertAfterPage(page, formID) {
  const field = page.querySelector('.gfield.after');
  if (!field) return;

  const footer = page.querySelector('.gform_page_footer');
  const holder = document.querySelector(`.form-holder-${formID}`);
  const gformFooter = page.querySelector('.gform_footer');

  if (footer) {
    footer.append(field);
  } else if (holder) {
    holder.after(field);
    field.classList.add('form-disclaimer', `form-disclaimer-${formID}`);
  } else if (gformFooter) {
    gformFooter.append(field);
  }

  field.style.display = '';
}

export function formatMoney(value) {
  return Number(value).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  });
}
